import { useMemo } from 'react';
import {
  Form,
  LocationType,
  AccessoriesFormErrors,
} from 'app/hooks/useGlobalForm';
import useLocalStorageState from 'use-local-storage-state';
import { rentalRangeError, todayIso } from 'utils/rentalDates';
import {
  checkVacationHomeFieldsErrors,
  checkVacationHomeZipError,
} from 'utils/validators/vacationHomeValidator';
import { isLocationComplete } from 'utils/validators/locationValidator';
import { SystemParameterInterface } from 'ks-common';
import { normalizeInput } from 'utils/validators/addressMatcher';
import { isNoteTooLong } from 'utils/customerNotes';

export interface Validations {
  dateRangeError?: string;
  deliveryDateIsToday?: boolean;
  requiredAccessoriesFormErrors?: Partial<AccessoriesFormErrors>;
  accessoriesFormErrors?: Partial<AccessoriesFormErrors>;
  deliveryZipError?: string;
  returnZipError?: string;
  nameTagsError: boolean;
  notesTooLong: boolean;
  cartLocationsIncomplete: boolean;
}

type FormValidations = (
  products: Partial<Form>[],
  currentProduct?: Partial<Form>,
) => Validations;

const useGlobalFormValidations: FormValidations = (
  products,
  currentProduct,
) => {
  const [systemParams] =
    useLocalStorageState<SystemParameterInterface[]>('systemParams');

  const bannedList = useMemo(
    () =>
      systemParams
        ?.find(param => param.ParamName === 'Banned_List')
        ?.ParamValue?.split(';'),
    [systemParams],
  );

  const normalizedBannedList = useMemo(
    () =>
      bannedList
        ?.map(addr => normalizeInput(addr))
        .filter(addr => addr.length > 0),
    [bannedList],
  );

  const deliveryDateIsToday = currentProduct?.deliveryDate === todayIso();

  const dateRangeError = useMemo(() => {
    if (deliveryDateIsToday) {
      return 'Please review and enter a valid delivery date.';
    }

    return rentalRangeError({
      deliveryDate: currentProduct?.deliveryDate ?? null,
      returnDate: currentProduct?.returnDate ?? null,
    });
  }, [currentProduct]);

  const requiredAccessoriesFormErrors = useMemo(() => {
    const errors: Partial<AccessoriesFormErrors> = {};

    if (!currentProduct?.accessories?.nameTag) {
      errors.nameTag = 'Please enter your family name';
    }

    if (!currentProduct?.deliveryLocation?.location) {
      errors.deliveryLocation = 'Please select a delivery location';
    }

    if (!currentProduct?.returnLocation?.location) {
      errors.returnLocation = 'Please select a return location';
    }

    if (!currentProduct?.deliveryLocation?.time) {
      errors.deliveryTime = 'Please select a delivery location time';
    }

    if (!currentProduct?.returnLocation?.time) {
      errors.returnTime = 'Please select a return location time';
    }

    if (
      currentProduct?.deliveryLocation?.location === LocationType.VacationHome
    ) {
      const deliveryVacationHome =
        currentProduct?.deliveryLocation?.vacationHome;

      const deliveryVacationErrors = checkVacationHomeFieldsErrors({
        isSubmit: true,
        vacationHomeForm: deliveryVacationHome,
        bannedList: normalizedBannedList,
      });

      if (deliveryVacationErrors) {
        errors.deliveryVacationHomeFields = deliveryVacationErrors;
      }
    }

    if (
      currentProduct?.returnLocation?.location === LocationType.VacationHome
    ) {
      const returnVacationHome = currentProduct?.returnLocation?.vacationHome;

      const returnVacationErrors = checkVacationHomeFieldsErrors({
        isSubmit: true,
        vacationHomeForm: returnVacationHome,
        bannedList: normalizedBannedList,
      });

      if (returnVacationErrors) {
        errors.returnVacationHomeFields = returnVacationErrors;
      }
    }

    if (Object.keys(errors).length) {
      return errors;
    }

    return undefined;
  }, [currentProduct]);

  const accessoriesFormErrors = useMemo(() => {
    const errors: Partial<AccessoriesFormErrors> = {};

    if (
      currentProduct?.deliveryLocation?.location === LocationType.Resort &&
      !currentProduct?.deliveryLocation?.resort
    ) {
      errors.deliveryLocationResort = 'Please select a resort';
    }
    if (
      currentProduct?.returnLocation?.location === LocationType.Resort &&
      !currentProduct?.returnLocation?.resort
    ) {
      errors.returnLocationResort = 'Please select a resort';
    }

    if (
      currentProduct?.deliveryLocation?.location === LocationType.VacationHome
    ) {
      const deliveryVacationHome =
        currentProduct?.deliveryLocation?.vacationHome;

      const deliveryVacationErrors = checkVacationHomeFieldsErrors({
        isSubmit: false,
        vacationHomeForm: deliveryVacationHome,
        bannedList: normalizedBannedList,
      });

      if (deliveryVacationErrors) {
        errors.deliveryVacationHomeFields = deliveryVacationErrors;
      }
    }

    if (
      currentProduct?.returnLocation?.location === LocationType.VacationHome
    ) {
      const returnVacationHome = currentProduct?.returnLocation?.vacationHome;

      const returnVacationErrors = checkVacationHomeFieldsErrors({
        isSubmit: false,
        vacationHomeForm: returnVacationHome,
        bannedList: normalizedBannedList,
      });

      if (returnVacationErrors) {
        errors.returnVacationHomeFields = returnVacationErrors;
      }
    }

    if (Object.keys(errors).length) {
      return errors;
    }

    return undefined;
  }, [currentProduct]);

  const deliveryZipError = useMemo(() => {
    if (
      currentProduct?.deliveryLocation?.location !== LocationType.VacationHome
    )
      return '';

    return checkVacationHomeZipError({
      vacationHomeForm: currentProduct?.deliveryLocation?.vacationHome,
      systemParams,
    });
  }, [currentProduct]);

  const returnZipError = useMemo(() => {
    if (currentProduct?.returnLocation?.location !== LocationType.VacationHome)
      return '';

    return checkVacationHomeZipError({
      vacationHomeForm: currentProduct?.returnLocation?.vacationHome,
      systemParams,
    });
  }, [currentProduct]);

  const nameTagsError = useMemo(() => {
    return products.some(prod => !prod.accessories?.nameTag);
  }, [products]);

  const notesTooLong = useMemo(
    () =>
      products.some(prod => isNoteTooLong(prod.accessories?.commentsAndNotes)),
    [products],
  );

  const cartLocationsIncomplete = useMemo(
    () =>
      products.some(
        prod =>
          !isLocationComplete(prod.deliveryLocation, normalizedBannedList) ||
          !isLocationComplete(prod.returnLocation, normalizedBannedList),
      ),
    [products, normalizedBannedList],
  );

  const validations = {
    dateRangeError,
    deliveryDateIsToday,
    requiredAccessoriesFormErrors,
    accessoriesFormErrors,
    deliveryZipError,
    returnZipError,
    nameTagsError,
    notesTooLong,
    cartLocationsIncomplete,
  };

  return validations;
};

export default useGlobalFormValidations;
